import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import {
  useEditProjectReportTemplate,
  useRetrieveCurrentPlanzone,
  useRetrieveProjectReportTemplate,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { DragAndDrop, Draggable } from "@common/dragAndDrop";
import { SelectList } from "@common/selectList";
import { ToggleArrowIcon } from "@common/assets/toggleArrowIcon";
import { SecondaryCriteriaTemplate } from "@scenes/secondaryCriteriaTemplate";
import { Props } from "./types";
import { ReportPreview } from "./reportPreview";
import { MoveIcon } from "./textFields/moveIcon";
import styles from "./styles.scss";
import {
  Identifiable,
  ProjectReportField,
  ProjectReportTemplate,
} from "@planzone/dto";

function ProjectReportTemplatePage(_: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    reportTemplates,
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    retrieveProjectReportTemplate,
  } = useRetrieveProjectReportTemplate(planzone);
  const {
    isRequestPending: isEditProjectReportTemplateRequestPending,
    editProjectReportTemplate,
  } = useEditProjectReportTemplate(planzone);
  const {
    template: criteriaTemplate,
    isRequestPending: isCriteriaRequestPending,
    isRequestSuccess: isCriteriaRequestSuccess,
    isRequestFailure: isCriteriaRequestFailure,
    retrieveSecondaryCriteriaTemplate,
  } = useRetrieveSecondaryCriteriaTemplate(planzone.uuid, "projectReport");
  const [localTemplates, setLocalTemplates] = React.useState<
    Identifiable<ProjectReportTemplate>[]
  >([]);
  const [selectedTemplateId, setSelectedTemplateId] =
    React.useState<string>("");

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveProjectReportTemplate(planzone);
  }, [planzone.uuid]);

  React.useEffect(() => {
    !isCriteriaRequestPending &&
      !isCriteriaRequestSuccess &&
      !isCriteriaRequestFailure &&
      retrieveSecondaryCriteriaTemplate({
        namespace: planzone.uuid,
        scope: "planzone",
        elementType: "projectReport",
      });
  }, [planzone.uuid]);

  React.useEffect(() => {
    setLocalTemplates(reportTemplates);
    if (
      reportTemplates.length > 0 &&
      !reportTemplates.find((t) => t.uuid === selectedTemplateId)
    ) {
      setSelectedTemplateId(reportTemplates[0].uuid);
    }
  }, [reportTemplates]);

  const selectedTemplate = localTemplates.find(
    (t) => t.uuid === selectedTemplateId,
  );

  function updateSelectedTemplate(
    updater: (
      t: Identifiable<ProjectReportTemplate>,
    ) => Identifiable<ProjectReportTemplate>,
  ) {
    setLocalTemplates((prev) =>
      prev.map((t) => (t.uuid === selectedTemplateId ? updater(t) : t)),
    );
  }

  function addTemplate() {
    const newTemplate: Identifiable<ProjectReportTemplate> = {
      uuid: uuidv4(),
      name: "",
      indicators: [],
      textFields: [{ name: "", type: "text", order: 0 }],
    };
    setLocalTemplates((prev) => [...prev, newTemplate]);
    setSelectedTemplateId(newTemplate.uuid);
  }

  function removeTemplate(uuid: string) {
    if (localTemplates.length <= 1) return;
    const next = localTemplates.filter((t) => t.uuid !== uuid);
    setLocalTemplates(next);
    if (selectedTemplateId === uuid) {
      setSelectedTemplateId(next[0].uuid);
    }
  }

  function save(overrideTemplates?: Identifiable<ProjectReportTemplate>[]) {
    const templatesToSave = overrideTemplates || localTemplates;
    !isEditProjectReportTemplateRequestPending &&
      editProjectReportTemplate({
        planzone,
        reportTemplates: templatesToSave,
      });
  }

  const textFields = selectedTemplate ? selectedTemplate.textFields : [];
  const indicatorsFields = selectedTemplate ? selectedTemplate.indicators : [];

  return (
    <div className={styles.projectReportTemplatePage}>
      <div className={styles.templateToolbar}>
        <SelectList
          rootClassName={styles.templateSelector}
          listClassName={styles.templateListRoot}
        >
          {({ isOpen, select }) => ({
            input: (
              <div
                className={classNames(styles.templateSelectorInput, {
                  [styles.templateSelectorOpen]: isOpen,
                })}
              >
                <p>
                  {selectedTemplate?.name ||
                    t(
                      "utilityPage.projectReportTemplatePage.templateNamePlaceholder",
                    )}
                </p>
                <ToggleArrowIcon className={styles.templateSelectorIcon} />
              </div>
            ),
            list: (
              <div className={styles.templateList}>
                {localTemplates.map((tmpl) => (
                  <div
                    key={tmpl.uuid}
                    className={classNames(styles.templateListItem, {
                      [styles.templateListItemSelected]:
                        tmpl.uuid === selectedTemplateId,
                    })}
                    onClick={() => select(setSelectedTemplateId(tmpl.uuid))}
                  >
                    <p>
                      {tmpl.name ||
                        t(
                          "utilityPage.projectReportTemplatePage.templateNamePlaceholder",
                        )}
                    </p>
                  </div>
                ))}
              </div>
            ),
          })}
        </SelectList>
        {localTemplates.length > 1 && (
          <button
            className={styles.removeTemplateButton}
            type="button"
            onClick={() => removeTemplate(selectedTemplateId)}
          >
            &times;
          </button>
        )}
        <button
          className={styles.addTemplateButton}
          type="button"
          onClick={addTemplate}
        >
          +
        </button>
      </div>
      {selectedTemplate && (
        <div className={styles.templateNameGroup}>
          <h2 className={styles.title}>
            {t("utilityPage.projectReportTemplatePage.templateName")}
          </h2>
          <input
            className={styles.templateNameInput}
            value={selectedTemplate.name}
            placeholder={t(
              "utilityPage.projectReportTemplatePage.templateNamePlaceholder",
            )}
            onChange={({ target }) =>
              setLocalTemplates((prev) =>
                prev.map((t) =>
                  t.uuid === selectedTemplateId
                    ? { ...t, name: target.value }
                    : t,
                ),
              )
            }
          />
        </div>
      )}

      {selectedTemplate && (
        <ReportPreview
          indicators={indicatorsFields}
          textFields={textFields}
          criteriaTemplate={criteriaTemplate}
          isDisabled={isEditProjectReportTemplateRequestPending}
          editIndicators={(indicators: ProjectReportField[]) =>
            updateSelectedTemplate((t) => ({ ...t, indicators }))
          }
          submitIndicators={(indicators: ProjectReportField[]) => {
            const updated = localTemplates.map((t) =>
              t.uuid === selectedTemplateId ? { ...t, indicators } : t,
            );
            setLocalTemplates(updated);
            save(updated);
          }}
          editFields={(fields: ProjectReportField[]) =>
            updateSelectedTemplate((t) => ({ ...t, textFields: fields }))
          }
          submitFields={(fields: ProjectReportField[]) => {
            const updated = localTemplates.map((t) =>
              t.uuid === selectedTemplateId ? { ...t, textFields: fields } : t,
            );
            setLocalTemplates(updated);
            save(updated);
          }}
        />
      )}
      {criteriaTemplate.length > 0 && (
        <div className={styles.criteriaSection}>
          <h2 className={styles.title}>
            {t("utilityPage.projectReportTemplatePage.criteria")}
          </h2>
          <DragAndDrop>
            <div className={styles.criteriaList}>
              {criteriaTemplate.map((criteria) => {
                const alreadyUsed = textFields.some(
                  (f) => f.criteriaId === criteria.uuid,
                );
                return (
                  <Draggable
                    key={criteria.uuid}
                    data={criteria}
                    type="addCriteria"
                    isDisabled={alreadyUsed}
                  >
                    <div
                      className={classNames(styles.criteriaItem, {
                        [styles.criteriaItemDisabled]: alreadyUsed,
                      })}
                    >
                      <MoveIcon className={styles.criteriaIcon} />
                      {criteria.name}
                    </div>
                  </Draggable>
                );
              })}
            </div>
          </DragAndDrop>
        </div>
      )}
      <SecondaryCriteriaTemplate
        namespace={planzone.uuid}
        scope="planzone"
        elementType="projectReport"
      />
    </div>
  );
}

export { ProjectReportTemplatePage };
